import { useLayoutEffect } from 'react';
import { scrollPaneAt } from '../OpenCard/scrollPane';

/**
 * A list's filter header over the rows it filters, as the card's tab bar.
 * Marks `dock` (the header, or what holds it) as its rows scroll:
 *
 * - `<base>--over` while rows are scrolled under it — the only time it casts
 *   a shadow;
 * - `<base>--stuck` while it is stuck to the top of what scrolls — square
 *   corners there.
 *
 * `area` is where the rows scroll: the area itself where it scrolls, or else
 * the one it is in. A header above that area never moves and only shades; a
 * header in it (sticky, inside a page that scrolls) shades once stuck.
 *
 * Also puts that area's top padding on the dock as `--nx-pane-inset`: a sticky
 * element stops short of it, so a header that sticks at
 * `top: calc(-1 * var(--nx-pane-inset))` meets the area's very edge.
 */
const useHeaderShade = (dock: HTMLElement | null, area: HTMLElement | null, base: string) => {
    useLayoutEffect(() => {
        const pane = area && scrollPaneAt(area);
        if (!dock || !pane) return undefined;
        const inArea = pane.contains(dock);
        dock.style.setProperty('--nx-pane-inset', `${parseFloat(window.getComputedStyle(pane).paddingTop) || 0}px`);
        const check = () => {
            const scrolled = pane.scrollTop > 0;
            const stuck = inArea && scrolled
                && dock.getBoundingClientRect().top <= pane.getBoundingClientRect().top + 0.5;
            dock.classList.toggle(`${base}--stuck`, stuck);
            dock.classList.toggle(`${base}--over`, inArea ? stuck : scrolled);
        };
        check();
        pane.addEventListener('scroll', check, { passive: true });
        return () => pane.removeEventListener('scroll', check);
    }, [dock, area, base]);
};

export default useHeaderShade;
